import { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Building2, Lock, Mail, User, Phone, CreditCard, Eye, EyeOff, Users, Landmark, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import AltchaWidget from "../components/AltchaWidget";

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, login, register, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState(null); // null, 'contribuyente', 'funcionario'
  const justLoggedIn = useRef(false);
  
  // Login form
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  
  // Register form
  const [registerForm, setRegisterForm] = useState({
    email: "",
    password: "",
    nombre: "",
    apellido: "",
    cedula: "",
    telefono: ""
  });

  // ALTCHA anti-bot payload (PoW resuelto)
  const [altchaPayload, setAltchaPayload] = useState("");

  // Aceptación obligatoria de términos y privacidad (registro)
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Auto-cerrar sesión al llegar a /login con sesión activa (permite cambiar de cuenta)
  useEffect(() => {
    if (user && !justLoggedIn.current) {
      logout();
    }
  }, []);

  // Navegar al dashboard correcto después de login exitoso
  useEffect(() => {
    if (user && justLoggedIn.current) {
      justLoggedIn.current = false;
      if (user.role === "contribuyente") {
        navigate("/portal/dashboard", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!altchaPayload) {
      toast.error("Por favor complete la verificación anti-bot ALTCHA");
      return;
    }
    setLoading(true);
    justLoggedIn.current = true;
    const result = await login(loginForm.email, loginForm.password, altchaPayload, userType);
    if (!result.success) {
      justLoggedIn.current = false;
      setAltchaPayload(""); // Forzar re-verificación
    } else if (result.mfaRequired) {
      justLoggedIn.current = false;
      navigate("/mfa-challenge", { replace: true });
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!altchaPayload) {
      toast.error("Por favor complete la verificación anti-bot ALTCHA");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Debe aceptar los Términos y Condiciones y la Política de Privacidad para continuar");
      return;
    }
    setLoading(true);
    justLoggedIn.current = true;
    const role = userType === "contribuyente" ? "contribuyente" : "funcionario";
    const result = await register({
      ...registerForm,
      role,
      altcha_payload: altchaPayload,
      terms_accepted: acceptedTerms,
      terms_version: "1.0",
      privacy_version: "1.0",
    });
    if (result.success) {
      navigate(role === "contribuyente" ? "/portal/dashboard" : "/dashboard", { replace: true });
    } else {
      justLoggedIn.current = false;
      setAltchaPayload("");
    }
    setLoading(false);
  };

  const handleBack = () => {
    setUserType(null);
  };

  // Initial screen with user type selection
  if (!userType) {
    return (
      <div className="min-h-screen flex login-pattern">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white">
          <div className="max-w-md text-center animate-fade-in">
            <div className="flex items-center justify-center mb-8">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <Building2 className="w-10 h-10 text-yellow-400" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sistema de Recaudación Tributaria Municipal
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Plataforma integral para la gestión de tributos municipales
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-yellow-400 font-semibold mb-1">Catastro</div>
                <div className="text-slate-400">Gestión de propiedades</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-green-400 font-semibold mb-1">LAE</div>
                <div className="text-slate-400">Licencias comerciales</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-blue-400 font-semibold mb-1">Liquidaciones</div>
                <div className="text-slate-400">Cálculo de impuestos</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-white font-semibold mb-1">Recaudación</div>
                <div className="text-slate-400">Control de pagos</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - User Type Selection */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
          <Card className="w-full max-w-md shadow-xl border-0 animate-fade-in" data-testid="user-type-card">
            <CardHeader className="text-center pb-2">
              <div className="lg:hidden flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-blue-800 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-yellow-400" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bienvenido Siret Demon
              </CardTitle>
              <CardDescription className="text-slate-500">
                Seleccione su tipo de usuario para continuar
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4 pt-4">
              <Button 
                onClick={() => setUserType('contribuyente')}
                className="w-full h-24 bg-green-600 hover:bg-green-700 text-white flex flex-col items-center justify-center gap-2 rounded-xl"
                data-testid="btn-contribuyente"
              >
                <Users className="w-8 h-8" />
                <span className="text-lg font-semibold">Soy Contribuyente</span>
              </Button>
              
              <Button 
                onClick={() => setUserType('funcionario')}
                className="w-full h-24 bg-blue-800 hover:bg-blue-900 text-white flex flex-col items-center justify-center gap-2 rounded-xl"
                data-testid="btn-funcionario"
              >
                <Landmark className="w-8 h-8" />
                <span className="text-lg font-semibold">Soy Funcionario</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex login-pattern">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white">
        <div className="max-w-md text-center animate-fade-in">
          <div className="flex items-center justify-center mb-8">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <Building2 className="w-10 h-10 text-yellow-400" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sistema de Recaudación Tributaria Municipal
          </h1>
          <p className="text-lg text-slate-300 mb-8">
            Plataforma integral para la gestión de tributos municipales
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="text-yellow-400 font-semibold mb-1">Catastro</div>
              <div className="text-slate-400">Gestión de propiedades</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="text-green-400 font-semibold mb-1">LAE</div>
              <div className="text-slate-400">Licencias comerciales</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="text-blue-400 font-semibold mb-1">Liquidaciones</div>
              <div className="text-slate-400">Cálculo de impuestos</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="text-white font-semibold mb-1">Recaudación</div>
              <div className="text-slate-400">Control de pagos</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md shadow-xl border-0 animate-fade-in" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-between mb-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack}
                className="text-slate-500 hover:text-slate-700"
                data-testid="btn-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Volver
              </Button>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                userType === 'contribuyente' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {userType === 'contribuyente' ? 'Contribuyente' : 'Funcionario'}
              </div>
            </div>
            <div className="lg:hidden flex items-center justify-center mb-4">
              <div className="w-14 h-14 bg-blue-800 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-yellow-400" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {userType === 'contribuyente' ? 'Portal del Contribuyente' : 'Portal del Funcionario'}
            </CardTitle>
            <CardDescription className="text-slate-500">
              Accede al sistema de recaudación municipal
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Para contribuyentes: ahora con tabs de login y registro */}
            {userType === 'contribuyente' ? (
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="login" data-testid="tab-login-contribuyente">Iniciar Sesión</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register-contribuyente">Registrarse</TabsTrigger>
                </TabsList>

                {/* LOGIN CONTRIBUYENTE */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-slate-700">Correo Electrónico</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          data-testid="login-email"
                          type="email"
                          placeholder="correo@ejemplo.com"
                          className="pl-10"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-slate-700">Contraseña</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          data-testid="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <AltchaWidget onVerified={setAltchaPayload} testid="altcha-login-contribuyente" />
                    <Button
                      type="submit"
                      className="w-full text-white bg-green-600 hover:bg-green-700"
                      data-testid="login-submit"
                      disabled={loading || !altchaPayload}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                          Iniciando...
                        </span>
                      ) : "Iniciar Sesión"}
                    </Button>
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={() => navigate("/forgot-password")}
                        className="text-xs text-blue-700 font-semibold hover:underline"
                        data-testid="link-forgot-password"
                      >
                        ¿Olvidó su contraseña?
                      </button>
                    </div>
                  </form>
                </TabsContent>

                {/* REGISTRO CONTRIBUYENTE */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="reg-nombre-c" className="text-slate-700 text-sm">Nombre</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="reg-nombre-c"
                            data-testid="register-nombre"
                            placeholder="Nombre"
                            className="pl-10"
                            value={registerForm.nombre}
                            onChange={(e) => setRegisterForm({ ...registerForm, nombre: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="reg-apellido-c" className="text-slate-700 text-sm">Apellido</Label>
                        <Input
                          id="reg-apellido-c"
                          data-testid="register-apellido"
                          placeholder="Apellido"
                          value={registerForm.apellido}
                          onChange={(e) => setRegisterForm({ ...registerForm, apellido: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="reg-cedula-c" className="text-slate-700 text-sm">Cédula / RIF</Label>
                      <div className="relative">
                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="reg-cedula-c"
                          data-testid="register-cedula"
                          placeholder="V-12345678"
                          className="pl-10"
                          value={registerForm.cedula}
                          onChange={(e) => setRegisterForm({ ...registerForm, cedula: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="reg-email-c" className="text-slate-700 text-sm">Correo Electrónico</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="reg-email-c"
                          data-testid="register-email"
                          type="email"
                          placeholder="correo@ejemplo.com"
                          className="pl-10"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="reg-telefono-c" className="text-slate-700 text-sm">Teléfono <span className="text-slate-400">(opcional)</span></Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="reg-telefono-c"
                          data-testid="register-telefono"
                          placeholder="0414-1234567"
                          className="pl-10"
                          value={registerForm.telefono}
                          onChange={(e) => setRegisterForm({ ...registerForm, telefono: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="reg-password-c" className="text-slate-700 text-sm">Contraseña</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="reg-password-c"
                          data-testid="register-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Mínimo 8 caracteres"
                          className="pl-10 pr-10"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <AltchaWidget onVerified={setAltchaPayload} testid="altcha-register-contribuyente" />

                    {/* Aceptación obligatoria de Términos y Privacidad */}
                    <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                      <input
                        id="accept-terms-c"
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-green-600 cursor-pointer"
                        data-testid="accept-terms-checkbox"
                        required
                      />
                      <label htmlFor="accept-terms-c" className="text-xs text-slate-700 leading-snug cursor-pointer">
                        He leído y acepto los{" "}
                        <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-green-700 font-semibold underline" data-testid="link-terminos">
                          Términos y Condiciones de Uso
                        </a>{" "}
                        y la{" "}
                        <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-green-700 font-semibold underline" data-testid="link-privacidad">
                          Política de Privacidad
                        </a>{" "}
                        de SIRET. Esta aceptación quedará registrada en el audit log con timestamp y hash de la versión vigente del documento.
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full text-white bg-green-600 hover:bg-green-700"
                      data-testid="register-submit"
                      disabled={loading || !altchaPayload || !acceptedTerms}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                          Registrando...
                        </span>
                      ) : "Crear Cuenta"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            ) : (
              /* Para funcionarios: solo login (sin auto-registro). Las cuentas las crea el administrador. */
              <form onSubmit={handleLogin} className="space-y-4" data-testid="funcionario-login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-slate-700">Correo Electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      className="pl-10"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-slate-700">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <AltchaWidget onVerified={setAltchaPayload} testid="altcha-login-funcionario" />

                <Button
                  type="submit"
                  className="w-full text-white bg-blue-800 hover:bg-blue-900"
                  data-testid="login-submit"
                  disabled={loading || !altchaPayload}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Iniciando...
                    </span>
                  ) : "Iniciar Sesión"}
                </Button>

                <p className="text-center text-xs text-slate-500 pt-2 border-t border-slate-100 mt-4">
                  El acceso de funcionarios es <strong>controlado</strong>. Las cuentas son creadas por el administrador del sistema. Si requiere acceso, contacte al área de TI de su alcaldía.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
